import TitrePage from "../components/charte/TitrePage";
import locationApi from "../utilities/locationApi";
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  Box, Dialog, DialogContent, IconButton,
  InputAdornment, Table, TableBody, TableCell,
  TableContainer, TableHead, TablePagination, TableRow,
  TextField, Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";
import { loaderOff, loaderOn, logout, openSnackbar } from "../redux/actions";
import { useNavigate } from "react-router-dom";
import store from "../redux/store";
import { Niveau, getNiveaux } from "../utilities/locationService";
import { getUserdetails } from "../utilities/userService";
import { STYLE_TON } from "../theme/tribeTheme";
import { AIDE_RESEAU } from "../aide/textesAide";

/* ── Design tokens ── */
/* Couleurs de la charte (src/theme/tribeTheme.ts), mêmes clés qu'avant. */
const C = {
  bg:          'var(--c-bg)',
  card:        'var(--c-surface)',
  border:      'var(--c-border)',
  borderAccent:'#1F1E1B',
  accent:      '#1F1E1B',
  accentLight: '#57534A',
  accentMuted: 'rgba(31,30,27,0.07)',
  text:        '#1F1E1B',
  textSub:     '#57534A',
  textMuted:   '#57534A',
  textFaint:   '#57534A',
  success:     '#356A0F',
  warning:     '#7C5600',
  error:       '#A23A0D',
} as const;

/* ── Avatar ── */
const AVATAR_COLORS = ['#8b5cf6', '#10b981', '#f59e0b'];
function emailHash(email: string): number {
  let h = 0;
  for (let i = 0; i < email.length; i++) h = ((h << 5) - h + email.charCodeAt(i)) | 0;
  return Math.abs(h);
}
function CollectorAvatar({ email, size = 36 }: { email: string; size?: number }) {
  const color = AVATAR_COLORS[emailHash(email) % 3];
  return (
    <Box sx={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: color + '20', border: `2px solid ${color}50`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <Typography sx={{ fontSize: Math.round(size * 0.33), fontWeight: 800, color, lineHeight: 1 }}>
        {email.slice(0, 2).toUpperCase()}
      </Typography>
    </Box>
  );
}

/* ── SVG quality ring ── */
function QualityRing({ rate }: { rate: number }) {
  const r = 18;
  const circ = 2 * Math.PI * r;
  const dash = (Math.min(rate, 100) / 100) * circ;
  const color = rate >= 80 ? C.success : rate >= 60 ? C.warning : C.error;
  return (
    <svg width={48} height={48} viewBox="0 0 48 48" style={{ flexShrink: 0 }}>
      <circle cx={24} cy={24} r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={4} />
      <circle cx={24} cy={24} r={r} fill="none" stroke={color} strokeWidth={4}
        strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
        transform="rotate(-90 24 24)" />
      <text x={24} y={28} textAnchor="middle" fontSize={9} fontWeight="bold" fill={color}>
        {Math.round(rate)}%
      </text>
    </svg>
  );
}

/* ── Mini activity bars ── */
function ActivityBars({ days }: { days: number[] }) {
  const max = Math.max(...days, 1);
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: 28 }}>
      {days.map((v, i) => (
        <Box key={i} sx={{
          width: 8, borderRadius: '2px 2px 0 0',
          height: `${Math.max(2, Math.round((v / max) * 28))}px`,
          background: v > 0 ? C.accent : 'rgba(255,255,255,0.06)',
        }} />
      ))}
    </Box>
  );
}

/* ── KPI card ── */
function KpiCard({ label, value, color, delta }: {
  label: string; value: string | number; color?: string;
  delta?: { value: number; suffix?: string };
}) {
  return (
    <Box sx={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '10px', p: 2.5 }}>
      <Typography sx={{ fontSize: 11, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600, mb: 1 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 28, fontWeight: 800, color: color || C.text, lineHeight: 1, mb: delta !== undefined ? 0.75 : 0 }}>
        {value}
      </Typography>
      {delta !== undefined && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: delta.value >= 0 ? C.success : C.error }}>
            {delta.value >= 0 ? '▲' : '▼'} {Math.abs(delta.value)}{delta.suffix || ''}
          </Typography>
          <Typography sx={{ fontSize: 11, color: C.textMuted }}>vs sem. préc.</Typography>
        </Box>
      )}
    </Box>
  );
}

/* ── Niveaux ──
   Le niveau d'un Spotter est celui du serveur (current_level de /user/all),
   attribué chaque nuit selon le référentiel GET /niveaux (décision D7). Il
   n'est plus recalculé ici : deux calculs divergeaient. Seule la couleur,
   choix d'affichage, reste locale. */
const COULEURS_NIVEAU: Record<number, string> = {
  1: '#94a3b8', 2: '#60a5fa', 3: '#a78bfa', 4: '#f59e0b', 5: '#34d399',
};
const couleurNiveau = (n: number) => COULEURS_NIVEAU[n] ?? '#94a3b8';
const NiveauxCtx = createContext<Niveau[]>([]);

function useNomNiveau(level: number | null): string {
  const niveaux = useContext(NiveauxCtx);
  if (level == null) return 'inconnu';
  return niveaux.find(n => n.niveau === level)?.nom ?? '';
}

function LevelBadge({ level }: { level: number | null }) {
  const nom = useNomNiveau(level);
  const color = level == null ? C.textMuted : couleurNiveau(level);
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.5,
      px: 1, py: 0.3, borderRadius: '5px',
      background: color + '18', border: `1px solid ${color}40`,
    }}>
      <Typography sx={{ fontSize: 10, fontWeight: 800, color }}>{level == null ? 'N?' : `N${level}`}</Typography>
      {nom && <Typography sx={{ fontSize: 10, fontWeight: 600, color: color + 'cc' }}>{nom}</Typography>}
    </Box>
  );
}

/* ── Types ── */
interface CollectorCard {
  email: string;
  poisCount: number;
  approvedCount: number;
  approvalRate: number;
  city?: string;
  firstSeen: Date;
  lastSeen: Date;
  activityDays: number[];
  topCategories: { name: string; pct: number }[];
  isInactive14d: boolean;
  isNew: boolean;
  tips: number;
  /** Niveau serveur, null si le Spotter n'est pas trouvé dans /user/all. */
  level: number | null;
  recentLocs: any[];
}

/* ── Build collector cards from raw locations ── */
function buildCollectors(locations: any[], niveauParEmail: Map<string, number>): CollectorCard[] {
  const now = new Date();
  const day14ago = new Date(now.getTime() - 14 * 86400000);
  const day30ago = new Date(now.getTime() - 30 * 86400000);

  const map = new Map<string, any[]>();
  locations.forEach((loc: any) => {
    const email = loc.createdBy;
    if (!email) return;
    if (!map.has(email)) map.set(email, []);
    map.get(email)!.push(loc);
  });

  return Array.from(map.entries()).map(([email, locs]) => {
    const poisCount = locs.length;

    const approvedCount = locs.filter((l: any) => {
      const s = (l.status || l.statut || '').toLowerCase();
      return s === 'approved' || s === 'approuvé' || s === 'approuve';
    }).length;
    const approvalRate = poisCount > 0 ? (approvedCount / poisCount) * 100 : 0;

    const tips = locs.reduce((acc: number, l: any) => acc + (Number(l.token) || 0), 0);

    const dates = locs
      .map((l: any) => {
        const raw = l.createdOn || l.createdAt;
        return raw ? new Date(raw) : null;
      })
      .filter((d): d is Date => d instanceof Date && !isNaN(d.getTime()));

    const firstSeen = dates.length > 0 ? new Date(Math.min(...dates.map(d => d.getTime()))) : now;
    const lastSeen  = dates.length > 0 ? new Date(Math.max(...dates.map(d => d.getTime()))) : now;

    const activityDays = Array.from({ length: 7 }, (_, i) => {
      const dayStart = new Date(now);
      dayStart.setHours(0, 0, 0, 0);
      dayStart.setDate(dayStart.getDate() - (6 - i));
      const dayEnd = new Date(dayStart.getTime() + 86400000);
      return locs.filter((l: any) => {
        const raw = l.createdOn || l.createdAt;
        if (!raw) return false;
        const d = new Date(raw);
        return d >= dayStart && d < dayEnd;
      }).length;
    });

    const catCount: Record<string, number> = {};
    locs.forEach((l: any) => {
      const cat = l.locationType || l.type || l.category || 'Autre';
      catCount[cat] = (catCount[cat] || 0) + 1;
    });
    const topCategories = Object.entries(catCount)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([name, count]) => ({ name, pct: Math.round((count / poisCount) * 100) }));

    const cityLoc = locs.find((l: any) => l.city || l.ville || l.region);
    const city = cityLoc ? (cityLoc.city || cityLoc.ville || cityLoc.region) : undefined;

    const recentLocs = [...locs]
      .sort((a, b) => {
        const ta = new Date(a.createdOn || a.createdAt || 0).getTime();
        const tb = new Date(b.createdOn || b.createdAt || 0).getTime();
        return tb - ta;
      })
      .slice(0, 20);

    return {
      email, poisCount, approvedCount, approvalRate, city, firstSeen, lastSeen,
      activityDays, topCategories, tips, level: niveauParEmail.get(email.toLowerCase()) ?? null,
      recentLocs,
      isInactive14d: lastSeen < day14ago,
      isNew: firstSeen > day30ago,
    };
  });
}

/* ── Helpers ── */
function maskEmail(email: string): string {
  const [local, domain] = email.split('@');
  return local.slice(0, 3) + '***@' + (domain || '');
}

function nameFromEmail(email: string): string {
  const local = email.split('@')[0] || '';
  return local
    .replace(/[._-]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
    .slice(0, 22) || '—';
}

function fmtDate(d: Date): string {
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtDateShort(d: Date): string {
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
}

/* ── Status badge ── */
function StatusBadge({ card }: { card: CollectorCard }) {
  if (card.isInactive14d) {
    return (
      <Box sx={{ display: 'inline-flex', px: 1, py: 0.3, borderRadius: '5px', background: 'rgba(162,58,13,0.10)', border: '1px solid rgba(162,58,13,0.2)' }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: C.error }}>Inactif 14j</Typography>
      </Box>
    );
  }
  if (card.isNew) {
    return (
      <Box sx={{ display: 'inline-flex', px: 1, py: 0.3, borderRadius: '5px', background: 'rgba(53,106,15,0.10)', border: '1px solid rgba(53,106,15,0.2)' }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: C.success }}>Nouveau</Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ display: 'inline-flex', px: 1, py: 0.3, borderRadius: '5px', background: STYLE_TON.accent.bg, border: `1px solid ${STYLE_TON.accent.border}` }}>
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: STYLE_TON.accent.color }}>Actif</Typography>
    </Box>
  );
}

/* ── POI status chip ── */
function PoiStatusChip({ status }: { status: string }) {
  const s = (status || '').toLowerCase();
  const isApproved = s === 'approved' || s === 'approuvé' || s === 'approuve';
  const isRejected = s === 'rejected' || s === 'rejeté' || s === 'rejete';
  const color = isApproved ? C.success : isRejected ? C.error : C.warning;
  const label = isApproved ? 'Validé' : isRejected ? 'Rejeté' : 'En attente';
  return (
    <Box sx={{ display: 'inline-flex', px: 0.75, py: 0.2, borderRadius: '4px', background: color + '15', border: `1px solid ${color}30` }}>
      <Typography sx={{ fontSize: 10, fontWeight: 700, color }}>{label}</Typography>
    </Box>
  );
}

/* ── Detail modal ── */
function CollectorModal({ card, open, onClose }: { card: CollectorCard | null; open: boolean; onClose: () => void }) {
  const niveaux = useContext(NiveauxCtx);
  const nomNiveau = useNomNiveau(card?.level ?? null);
  if (!card) return null;
  const lvlColor = card.level == null ? C.textMuted : couleurNiveau(card.level);
  /* Prochain niveau actif du référentiel : on affiche ses critères, le
     serveur seul décide de la promotion. */
  const nextLvl = card.level == null ? null
    : niveaux.filter(n => n.actif && n.niveau > (card.level as number)).sort((x, y) => x.niveau - y.niveau)[0] ?? null;
  const criteresSuivant = nextLvl ? [
    nextLvl.minApprouves ? `${nextLvl.minApprouves} lieux validés` : null,
    nextLvl.tauxApprobationMin ? `taux ≥ ${nextLvl.tauxApprobationMin} %` : null,
    nextLvl.ancienneteJoursMin ? `${nextLvl.ancienneteJoursMin} j d'ancienneté` : null,
    nextLvl.categoriesMin ? `${nextLvl.categoriesMin} catégories` : null,
  ].filter(Boolean) as string[] : [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { background: C.card, border: `1px solid ${C.border}`, borderRadius: '12px', m: 2 } }}>
      <DialogContent sx={{ p: 0 }}>

        {/* ── Profil header ── */}
        <Box sx={{ p: 3, pb: 2.5, display: 'flex', alignItems: 'flex-start', gap: 2, borderBottom: `1px solid ${C.border}` }}>
          <CollectorAvatar email={card.email} size={56} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: C.text, mb: 0.75 }}>
              {card.email}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.75 }}>
              <LevelBadge level={card.level} />
              <StatusBadge card={card} />
              {card.city && (
                <Typography sx={{ fontSize: 11, color: C.textMuted }}>📍 {card.city}</Typography>
              )}
            </Box>
            <Typography sx={{ fontSize: 11, color: C.textFaint }}>
              Depuis {fmtDate(card.firstSeen)} · Dernière activité {fmtDate(card.lastSeen)}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: C.textMuted, '&:hover': { color: C.text } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* ── Stats row ── */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', borderBottom: `1px solid ${C.border}` }}>
          {[
            { label: 'Lieux au total',       value: card.poisCount.toString(),              color: C.text },
            { label: 'Taux validation',  value: `${Math.round(card.approvalRate)}%`,    color: card.approvalRate >= 80 ? C.success : card.approvalRate >= 60 ? C.warning : C.error },
            { label: 'Tips disponibles', value: card.tips > 0 ? card.tips.toLocaleString('fr-FR') : '—', color: C.warning },
            { label: 'Niveau',           value: card.level == null ? '—' : `N${card.level}${nomNiveau ? ` · ${nomNiveau}` : ''}`, color: lvlColor },
          ].map((stat, i) => (
            <Box key={i} sx={{ p: 2.5, borderRight: i < 3 ? `1px solid ${C.border}` : 'none' }}>
              <Typography sx={{ fontSize: 11, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.07em', mb: 0.5 }}>
                {stat.label}
              </Typography>
              <Typography sx={{ fontSize: 16, fontWeight: 800, color: stat.color }}>{stat.value}</Typography>
            </Box>
          ))}
        </Box>

        {/* ── Body: left / right ── */}
        <Box sx={{ p: 3, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>

          {/* Left: activity + categories + niveau progress */}
          <Box>
            <Typography sx={{ fontSize: 11, color: C.textFaint, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>
              Activité 7 jours
            </Typography>
            <ActivityBars days={card.activityDays} />

            {card.topCategories.length > 0 && (
              <Box sx={{ mt: 2.5 }}>
                <Typography sx={{ fontSize: 11, color: C.textFaint, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>
                  Catégories
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  {card.topCategories.map(cat => (
                    <Box key={cat.name} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontSize: 11, color: C.textSub, width: 90, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 0 }}>
                        {cat.name}
                      </Typography>
                      <Box sx={{ flex: 1, height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.06)' }}>
                        <Box sx={{ width: `${cat.pct}%`, height: '100%', borderRadius: 2, background: 'rgba(139,92,246,0.6)' }} />
                      </Box>
                      <Typography sx={{ fontSize: 10, color: C.textMuted, width: 28, textAlign: 'right', flexShrink: 0 }}>
                        {cat.pct}%
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Niveau progress */}
            <Box sx={{ mt: 2.5, p: 1.5, borderRadius: '8px', background: lvlColor + '10', border: `1px solid ${lvlColor}25` }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <QualityRing rate={card.approvalRate} />
                <Box>
                  <Typography sx={{ fontSize: 11, color: lvlColor, fontWeight: 700 }}>
                    {card.level == null ? 'Niveau inconnu' : `N${card.level}${nomNiveau ? ` — ${nomNiveau}` : ''}`}
                  </Typography>
                  <Typography sx={{ fontSize: 10, color: C.textMuted }}>
                    {card.approvedCount} lieux validés
                  </Typography>
                </Box>
              </Box>
              {nextLvl && (
                <Typography sx={{ fontSize: 10, color: C.textMuted }}>
                  N{nextLvl.niveau} ({nextLvl.nom}) : {criteresSuivant.length ? criteresSuivant.join(' · ') : 'aucun critère'}
                  {' '}— attribué par le serveur chaque nuit
                </Typography>
              )}
              {card.level != null && !nextLvl && (
                <Typography sx={{ fontSize: 10, color: lvlColor }}>Aucun niveau actif au-dessus</Typography>
              )}
            </Box>
          </Box>

          {/* Right: historique POIs */}
          <Box>
            <Typography sx={{ fontSize: 11, color: C.textFaint, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>
              Historique des lieux ({card.poisCount} au total · {card.recentLocs.length} affichés)
            </Typography>
            <Box sx={{
              maxHeight: 300, overflowY: 'auto',
              '&::-webkit-scrollbar': { width: 4 },
              '&::-webkit-scrollbar-thumb': { background: C.border, borderRadius: 2 },
            }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Date', 'Catégorie', 'Statut', 'Tips'].map(h => (
                      <TableCell key={h} sx={{
                        background: C.bg, color: C.accentLight, fontSize: 10, fontWeight: 700,
                        textTransform: 'uppercase', letterSpacing: '0.06em',
                        py: 0.75, borderBottom: `1px solid ${C.border}`,
                        position: 'sticky', top: 0, zIndex: 1,
                      }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {card.recentLocs.map((loc, i) => {
                    const raw = loc.createdOn || loc.createdAt;
                    const d = raw ? new Date(raw) : null;
                    const cat = loc.locationType || loc.type || loc.category || '—';
                    const locTips = Number(loc.token) || 0;
                    return (
                      <TableRow key={i} sx={{
                        '&:hover': { background: 'rgba(255,255,255,0.02)' },
                        '& .MuiTableCell-root': { borderBottom: `1px solid ${C.border}`, py: 0.75 },
                      }}>
                        <TableCell sx={{ color: C.textMuted, fontSize: 11, whiteSpace: 'nowrap' }}>
                          {d ? fmtDateShort(d) : '—'}
                        </TableCell>
                        <TableCell sx={{ color: C.textSub, fontSize: 11, maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {cat}
                        </TableCell>
                        <TableCell>
                          <PoiStatusChip status={loc.status || loc.statut || ''} />
                        </TableCell>
                        <TableCell sx={{ color: locTips > 0 ? C.warning : C.textFaint, fontSize: 11, fontWeight: locTips > 0 ? 700 : 400 }}>
                          {locTips > 0 ? locTips : '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {card.recentLocs.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4, color: C.textMuted, borderBottom: 'none', fontSize: 12 }}>
                        Aucun lieu
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

/* ── Period filter type ── */
type Period = '7d' | '30d' | 'all';

/* ── Main component ── */
export default function UserLocation() {
  const [locations, setLocations]       = useState<any[]>([]);
  const [activeQuests, setActiveQuests] = useState(0);
  const [searchQuery, setSearchQuery]   = useState('');
  const [period, setPeriod]             = useState<Period>('all');
  const [page, setPage]                 = useState(0);
  const [rowsPerPage, setRowsPerPage]   = useState(10);
  const [selectedCard, setSelectedCard] = useState<CollectorCard | null>(null);
  const [niveaux, setNiveaux]           = useState<Niveau[]>([]);
  const [niveauParEmail, setNiveauParEmail] = useState<Map<string, number>>(new Map());

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isLoggedIn     = useSelector((state: any) => state.auth.isLoggedIn);
  const isTokenExpired = useSelector((state: any) => state.auth.isTokenExpired);


  useEffect(() => { fetchData(); }, []);

  /* Niveau serveur de chaque Spotter (par e-mail) et noms du référentiel. */
  useEffect(() => {
    getNiveaux().then(setNiveaux).catch(() => setNiveaux([]));
    getUserdetails().then((res: any) => {
      const m = new Map<string, number>();
      (Array.isArray(res) ? res : []).forEach((u: any) => {
        if (u?.email && u.current_level != null) m.set(String(u.email).toLowerCase(), Number(u.current_level));
      });
      setNiveauParEmail(m);
    });
  }, []);

  const fetchData = async () => {
    const jwtToken = sessionStorage.getItem("jwtToken");
    if (!jwtToken) { dispatch(openSnackbar("Session introuvable. Veuillez vous reconnecter.", "red")); return; }
    dispatch(loaderOn());
    try {
      const [locRes, questRes] = await Promise.all([
        // Positions légères (POI non supprimés), au lieu de la collection complète (Z4-23).
        locationApi.get("/location/positions"),
        locationApi.get("/quest/all").catch(() => ({ data: [] })),
      ]);
      const locs = Array.isArray(locRes.data) ? locRes.data : [];
      // Page des Spotters : les imports OSM n'y comptent pas.
      setLocations(locs.filter((l: any) => l.createdBy !== "osm_import"));
      // /quest/all renvoie une Page ({ content }), pas un tableau.
      const quests = Array.isArray((questRes.data as any)?.content) ? (questRes.data as any).content : [];
      setActiveQuests(quests.filter((q: any) => {
        const s = (q.statut || q.status || '').toLowerCase();
        return s === 'publiee' || s === 'published' || s === 'active';
      }).length);
    } catch (err) {
      console.error("Error fetching data:", err);
    }
    dispatch(loaderOff());
  };

  /* ── Derived data ── */
  const allCards = useMemo(() => buildCollectors(locations, niveauParEmail), [locations, niveauParEmail]);

  const filteredCards = useMemo(() => {
    let cards = allCards;
    if (period !== 'all') {
      const cutoff = new Date(Date.now() - (period === '7d' ? 7 : 30) * 86400000);
      cards = cards.filter(c => c.lastSeen >= cutoff);
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      cards = cards.filter(c =>
        c.email.toLowerCase().includes(q) ||
        nameFromEmail(c.email).toLowerCase().includes(q)
      );
    }
    return [...cards].sort((a, b) => b.poisCount - a.poisCount);
  }, [allCards, period, searchQuery]);

  const kpis = useMemo(() => {
    const now = Date.now();
    const d7  = now - 7  * 86400000;
    const d14 = now - 14 * 86400000;

    // Les POI portent createdOn, pas createdAt : les deltas valaient toujours 0 (Z4-25).
    const last7 = locations.filter(l => l.createdOn && new Date(l.createdOn).getTime() >= d7);
    const prev7 = locations.filter(l => {
      if (!l.createdOn) return false;
      const t = new Date(l.createdOn).getTime();
      return t >= d14 && t < d7;
    });

    const last7Emails = new Set(last7.map((l: any) => l.createdBy));
    const prev7Emails = new Set(prev7.map((l: any) => l.createdBy));

    const approvedCount = locations.filter((l: any) => {
      const s = (l.status || l.statut || '').toLowerCase();
      return s === 'approved' || s === 'approuvé' || s === 'approuve';
    }).length;

    return {
      poisDelta:       last7.length - prev7.length,
      collectorsDelta: last7Emails.size - prev7Emails.size,
      validationRate:  locations.length > 0 ? Math.round((approvedCount / locations.length) * 100) : 0,
      inactiveCount:   allCards.filter(c => c.isInactive14d).length,
    };
  }, [locations, allCards]);

  const slicedCards = filteredCards.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  return (
    <NiveauxCtx.Provider value={niveaux}>
    <Box sx={{ p: 3, minHeight: '100vh', background: C.bg }}>
      {/* ── Header ── */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <TitrePage domaine="reseau" aide={AIDE_RESEAU.spotters}>Spotters</TitrePage>
          <Typography sx={{ fontSize: 13, color: C.textMuted, mt: 0.5 }}>
            Qui collecte, combien, avec quel taux de validation.
          </Typography>
        </Box>

        {/* Period + Search */}
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          {(['7d', '30d', 'all'] as Period[]).map(p => (
            <Box key={p} onClick={() => { setPeriod(p); setPage(0); }} sx={{
              px: 2, py: 0.75, borderRadius: '7px', cursor: 'pointer', fontSize: 12, fontWeight: 600,
              border: `1px solid ${period === p ? C.accentLight : 'rgba(255,255,255,0.08)'}`,
              background: period === p ? C.accentMuted : 'transparent',
              color: period === p ? C.accentLight : C.textMuted,
              transition: 'all 0.15s ease', userSelect: 'none',
            }}>
              {p === '7d' ? '7 jours' : p === '30d' ? '30 jours' : 'Tout'}
            </Box>
          ))}
          <TextField
            placeholder="Rechercher..." value={searchQuery}
            onChange={e => { setSearchQuery(e.target.value); setPage(0); }}
            variant="outlined" size="small"
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: C.textMuted, fontSize: 18 }} /></InputAdornment> }}
            sx={{
              width: 220,
              '& .MuiOutlinedInput-root': {
                background: C.card, color: C.text, borderRadius: '7px',
                '& fieldset': { borderColor: C.border },
                '&:hover fieldset': { borderColor: C.borderAccent },
                '&.Mui-focused fieldset': { borderColor: C.accentLight },
              },
            }}
          />
        </Box>
      </Box>

      {/* ── KPIs ── */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 2, mb: 3 }}>
        <KpiCard label="Spotters actifs" value={allCards.length} delta={{ value: kpis.collectorsDelta }} />
        <KpiCard label="Lieux au total" value={locations.length} delta={{ value: kpis.poisDelta }} />
        <KpiCard
          label="Taux de validation"
          value={`${kpis.validationRate}%`}
          color={kpis.validationRate >= 80 ? C.success : kpis.validationRate >= 60 ? C.warning : C.error}
        />
        <KpiCard label="Quêtes actives" value={activeQuests} color={C.accentLight} />
        <KpiCard
          label="Inactifs 14j"
          value={kpis.inactiveCount}
          color={kpis.inactiveCount > 0 ? C.error : C.textMuted}
        />
      </Box>

      {/* ── Table ── */}
      <Box sx={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '10px', overflow: 'hidden' }}>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {[
                  { label: '#',                 width: 48  },
                  { label: 'Email',             width: 200 },
                  { label: 'Nom',               width: 140 },
                  { label: 'Niveau',            width: 150 },
                  { label: 'Lieux au total',        width: 100 },
                  { label: 'Taux validation',   width: 140 },
                  { label: 'Tips disponibles',  width: 130 },
                  { label: 'Dernière activité', width: 140 },
                  { label: 'Statut',            width: 110 },
                ].map(col => (
                  <TableCell key={col.label} sx={{
                    background: C.card, color: C.accentLight, fontWeight: 700,
                    fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em',
                    borderBottom: `1px solid ${C.border}`, width: col.width, whiteSpace: 'nowrap',
                  }}>
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {slicedCards.map((card, index) => (
                <TableRow
                  key={card.email}
                  onClick={() => setSelectedCard(card)}
                  sx={{
                    cursor: 'pointer',
                    background: 'rgba(255,255,255,0.01)',
                    '&:hover': { background: 'rgba(139,92,246,0.05)' },
                    '& .MuiTableCell-root': { borderBottom: `1px solid ${C.border}`, py: 1.25 },
                  }}
                >
                  {/* # */}
                  <TableCell>
                    <Typography sx={{ fontSize: 12, color: C.textMuted, fontWeight: 600 }}>
                      {page * rowsPerPage + index + 1}
                    </Typography>
                  </TableCell>

                  {/* Email */}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CollectorAvatar email={card.email} size={30} />
                      <Typography sx={{ fontSize: 12, color: C.text, whiteSpace: 'nowrap' }}>
                        {maskEmail(card.email)}
                      </Typography>
                    </Box>
                  </TableCell>

                  {/* Nom */}
                  <TableCell>
                    <Typography sx={{ fontSize: 12, color: C.textSub }}>
                      {nameFromEmail(card.email)}
                    </Typography>
                  </TableCell>

                  {/* Niveau */}
                  <TableCell>
                    <LevelBadge level={card.level} />
                  </TableCell>

                  {/* POIs total */}
                  <TableCell>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                      {card.poisCount}
                    </Typography>
                    {card.poisCount > 0 && (
                      <Typography sx={{ fontSize: 10, color: C.success }}>
                        {card.approvedCount} validés
                      </Typography>
                    )}
                  </TableCell>

                  {/* Taux validation */}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 52, height: 4, borderRadius: '2px', background: 'rgba(255,255,255,0.06)', overflow: 'hidden', flexShrink: 0 }}>
                        <Box sx={{
                          width: `${Math.min(card.approvalRate, 100)}%`, height: '100%', borderRadius: '2px',
                          background: card.approvalRate >= 80 ? C.success : card.approvalRate >= 60 ? C.warning : C.error,
                        }} />
                      </Box>
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: card.approvalRate >= 80 ? C.success : card.approvalRate >= 60 ? C.warning : C.error }}>
                        {Math.round(card.approvalRate)}%
                      </Typography>
                    </Box>
                  </TableCell>

                  {/* Tips disponibles */}
                  <TableCell>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: card.tips > 0 ? C.warning : C.textFaint }}>
                      {card.tips > 0 ? card.tips.toLocaleString('fr-FR') : '—'}
                    </Typography>
                  </TableCell>

                  {/* Dernière activité */}
                  <TableCell>
                    <Typography sx={{ fontSize: 12, color: C.textSub }}>
                      {fmtDate(card.lastSeen)}
                    </Typography>
                  </TableCell>

                  {/* Statut */}
                  <TableCell>
                    <StatusBadge card={card} />
                  </TableCell>
                </TableRow>
              ))}
              {slicedCards.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} sx={{ textAlign: 'center', py: 6, color: C.textMuted, borderBottom: 'none' }}>
                    Aucun Spotter trouvé
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={filteredCards.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={e => { setRowsPerPage(+e.target.value); setPage(0); }}
          sx={{ color: C.textSub, borderTop: `1px solid ${C.border}` }}
        />
      </Box>

      {/* ── Detail modal ── */}
      <CollectorModal
        card={selectedCard}
        open={selectedCard !== null}
        onClose={() => setSelectedCard(null)}
      />
    </Box>
    </NiveauxCtx.Provider>
  );
}
